import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Car, Heart, TrendingUp, Lock, CheckCircle, Loader2 } from 'lucide-react';
import { sql } from '../../lib/db';

// ---------------------------------------------------------------------------
// PIN Screen
// ---------------------------------------------------------------------------

function PinScreen({ onUnlock }: { onUnlock: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === 'famdev') {
      localStorage.setItem('fam_dev_unlocked', '1');
      onUnlock();
    } else {
      setError('Incorrect PIN. Please try again.');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-6">
          <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center">
            <Lock className="w-7 h-7 text-indigo-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Dev Access</h1>
          <p className="text-sm text-gray-500 text-center">Enter the dev PIN to access the journey panel</p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            value={pin}
            onChange={(e) => { setPin(e.target.value); setError(''); }}
            placeholder="Enter PIN"
            className="w-full border border-gray-200 rounded-lg px-4 py-3 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-400"
            autoFocus
          />
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <button
            type="submit"
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SeedStatus = 'idle' | 'loading' | 'success' | 'error';

interface JourneyStep {
  title: string;
  description: string;
  link?: { label: string; href: string };
}

// ---------------------------------------------------------------------------
// Step item
// ---------------------------------------------------------------------------

function StepItem({
  number,
  step,
  seeded,
}: {
  number: number;
  step: JourneyStep;
  seeded: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        seeded ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
      }`}>
        {seeded ? <CheckCircle className="w-4 h-4" /> : number}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm">{step.title}</p>
        <p className="text-gray-500 text-sm mt-0.5">{step.description}</p>
        {step.link && (
          <a
            href={step.link.href}
            className="inline-block mt-2 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {step.link.label}
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Creator Tab
// ---------------------------------------------------------------------------

function CreatorTab() {
  const { user } = useUser();
  const [seedStatus, setSeedStatus] = useState<SeedStatus>('idle');
  const [seedError, setSeedError] = useState('');
  const [campaignId, setCampaignId] = useState<string | null>(null);

  const handleSeed = async () => {
    if (!user?.id) {
      setSeedError('Sign in first, then seed.');
      setSeedStatus('error');
      return;
    }
    if (!sql) {
      setSeedError('DB not connected — check VITE_DATABASE_URL in .env');
      setSeedStatus('error');
      return;
    }

    setSeedStatus('loading');
    setSeedError('');

    try {
      const userId = user.id;

      // Upsert profile
      await sql`
        INSERT INTO profiles (id, name, email)
        VALUES (${userId}, 'Test Creator', 'creator@fam.test')
        ON CONFLICT (id) DO UPDATE SET name = 'Test Creator', updated_at = now()
      `;

      // Clean up any prior test campaign contributions first, then the campaign itself
      await sql`
        DELETE FROM contributions
        WHERE car_goal_id IN (
          SELECT id FROM car_goals
          WHERE user_id = ${userId} AND title = 'Test: My First Family Car'
        )
      `;
      await sql`
        DELETE FROM car_goals
        WHERE user_id = ${userId} AND title = 'Test: My First Family Car'
      `;

      // Create active campaign (day 3 of 7)
      const rows = await sql`
        INSERT INTO car_goals (id, user_id, title, description, goal_amount, current_amount, image_url, status, created_at)
        VALUES (
          gen_random_uuid(), ${userId},
          'Test: My First Family Car',
          'This is a test campaign. I need a reliable family car to get my kids to school and get to work every day. I have saved $5,000 toward this goal.',
          28400, 4750,
          'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=1600',
          'active',
          now() - interval '3 days'
        )
        RETURNING id
      `;

      const newCampaignId = rows[0]?.id as string;
      setCampaignId(newCampaignId);

      // Insert 3 test contributions
      await sql`
        INSERT INTO contributions (car_goal_id, contributor_id, amount, type, ownership_percentage, created_at)
        VALUES
          (${newCampaignId}, null, 2500, 'gift', 0, now() - interval '2 days'),
          (${newCampaignId}, null, 1500, 'investment', 5.28, now() - interval '1 day'),
          (${newCampaignId}, null, 750, 'gift', 0, now() - interval '12 hours')
      `;

      setSeedStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSeedError(msg);
      setSeedStatus('error');
    }
  };

  const steps: JourneyStep[] = [
    {
      title: 'View your Dashboard',
      description: 'You\'re now a campaign creator. Your active campaign shows on your dashboard.',
      link: { label: 'Go to Dashboard', href: '/' },
    },
    {
      title: 'See Day 3 progress',
      description: 'Your campaign is on Day 3 of 7. The 7-dot progress bar shows 3 dots filled. You have 3 funders and $4,750 raised.',
    },
    {
      title: 'Share your campaign link',
      description: "Click 'Share Your Link' to copy your campaign URL.",
      link: { label: 'Go to Dashboard', href: '/' },
    },
    {
      title: 'View your campaign page',
      description: 'See exactly what your funders see.',
      link: campaignId
        ? { label: 'View Campaign', href: `/goal/${campaignId}` }
        : undefined,
    },
    {
      title: 'Delete & re-create',
      description: 'Your campaign has 3 funders so the delete button won\'t show. Reseed to start fresh.',
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="rounded-xl overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-rose-500 to-orange-500 px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <Car className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Campaign Creator Journey</h2>
            <p className="text-rose-100 text-sm">Create and manage a 7-day car funding campaign</p>
          </div>
        </div>
      </div>

      {/* DB warning */}
      {!sql && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          DB not connected — check VITE_DATABASE_URL in .env
        </div>
      )}

      {/* Seed button */}
      <div className="mb-6">
        <button
          onClick={handleSeed}
          disabled={seedStatus === 'loading'}
          className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white font-semibold rounded-lg hover:bg-rose-700 disabled:opacity-60 transition-colors"
        >
          {seedStatus === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
          Seed This Account (Creator)
        </button>

        {seedStatus === 'success' && (
          <div className="mt-3 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-emerald-800 font-medium text-sm">Creator journey ready! Follow the steps below.</span>
          </div>
        )}

        {seedStatus === 'error' && (
          <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {seedError}
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="space-y-5">
        {steps.map((step, i) => (
          <StepItem
            key={i}
            number={i + 1}
            step={step}
            seeded={seedStatus === 'success'}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gifter Tab
// ---------------------------------------------------------------------------

function GifterTab() {
  const { user } = useUser();
  const [seedStatus, setSeedStatus] = useState<SeedStatus>('idle');
  const [seedError, setSeedError] = useState('');

  const handleSeed = async () => {
    if (!user?.id) {
      setSeedError('Sign in first, then seed.');
      setSeedStatus('error');
      return;
    }
    if (!sql) {
      setSeedError('DB not connected — check VITE_DATABASE_URL in .env');
      setSeedStatus('error');
      return;
    }

    setSeedStatus('loading');
    setSeedError('');

    try {
      const userId = user.id;

      await sql`
        INSERT INTO profiles (id, name, email)
        VALUES (${userId}, 'Test Gifter', 'gifter@fam.test')
        ON CONFLICT (id) DO UPDATE SET name = 'Test Gifter', updated_at = now()
      `;

      // Gift to Jordan's campaign
      await sql`
        INSERT INTO contributions (car_goal_id, contributor_id, amount, type, ownership_percentage, created_at)
        VALUES ('550e8400-e29b-41d4-a716-446655440001', ${userId}, 300, 'gift', 0, now() - interval '1 day')
        ON CONFLICT DO NOTHING
      `;

      // Investment in Maya's campaign
      await sql`
        INSERT INTO contributions (car_goal_id, contributor_id, amount, type, ownership_percentage, created_at)
        VALUES ('550e8400-e29b-41d4-a716-446655440002', ${userId}, 1500, 'investment', 3.33, now() - interval '6 hours')
        ON CONFLICT DO NOTHING
      `;

      setSeedStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSeedError(msg);
      setSeedStatus('error');
    }
  };

  const steps: JourneyStep[] = [
    {
      title: 'Browse live campaigns',
      description: "Go to the campaigns page to see what's live.",
      link: { label: 'View Campaigns', href: '/campaigns' },
    },
    {
      title: 'Fund a campaign (gift)',
      description: "Click 'Fund this FAM' on any campaign. Choose Gift. Enter any amount >= $5. Use test card: 4242 4242 4242 4242, any future date, any CVC.",
    },
    {
      title: 'Be the first funder',
      description: "Find a campaign with $0 raised and fund it — you'll earn the First Funder badge.",
    },
    {
      title: 'See your name in the funders list',
      description: 'After payment, refresh the campaign page. Your name appears in the Funders section.',
    },
    {
      title: 'Check your seeded history',
      description: "Your profile already has 2 past contributions seeded: $300 gift to Jordan's campaign, $1,500 investment in Maya's.",
      link: { label: "View Jordan's Campaign", href: '/goal/550e8400-e29b-41d4-a716-446655440001' },
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="rounded-xl overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Gifter / Funder Journey</h2>
            <p className="text-indigo-100 text-sm">Support campaigns with gifts or investments</p>
          </div>
        </div>
      </div>

      {/* DB warning */}
      {!sql && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          DB not connected — check VITE_DATABASE_URL in .env
        </div>
      )}

      {/* Seed button */}
      <div className="mb-6">
        <button
          onClick={handleSeed}
          disabled={seedStatus === 'loading'}
          className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
        >
          {seedStatus === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
          Seed This Account (Gifter)
        </button>

        {seedStatus === 'success' && (
          <div className="mt-3 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-emerald-800 font-medium text-sm">Gifter journey ready! Follow the steps below.</span>
          </div>
        )}

        {seedStatus === 'error' && (
          <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {seedError}
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="space-y-5">
        {steps.map((step, i) => (
          <StepItem
            key={i}
            number={i + 1}
            step={step}
            seeded={seedStatus === 'success'}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Investor Tab
// ---------------------------------------------------------------------------

function InvestorTab() {
  const { user } = useUser();
  const [seedStatus, setSeedStatus] = useState<SeedStatus>('idle');
  const [seedError, setSeedError] = useState('');

  const handleSeed = async () => {
    if (!user?.id) {
      setSeedError('Sign in first, then seed.');
      setSeedStatus('error');
      return;
    }
    if (!sql) {
      setSeedError('DB not connected — check VITE_DATABASE_URL in .env');
      setSeedStatus('error');
      return;
    }

    setSeedStatus('loading');
    setSeedError('');

    try {
      const userId = user.id;

      await sql`
        INSERT INTO profiles (id, name, email)
        VALUES (${userId}, 'Test Investor', 'investor@fam.test')
        ON CONFLICT (id) DO UPDATE SET name = 'Test Investor', updated_at = now()
      `;

      await sql`
        INSERT INTO contributions (car_goal_id, contributor_id, amount, type, ownership_percentage, created_at)
        VALUES
          ('550e8400-e29b-41d4-a716-446655440001', ${userId}, 5000, 'investment', 17.6, now() - interval '5 days'),
          ('550e8400-e29b-41d4-a716-446655440002', ${userId}, 8500, 'investment', 18.9, now() - interval '3 days')
        ON CONFLICT DO NOTHING
      `;

      setSeedStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSeedError(msg);
      setSeedStatus('error');
    }
  };

  const steps: JourneyStep[] = [
    {
      title: 'Open the Investor Dashboard',
      description: 'Navigate to your investor portfolio.',
      link: { label: 'Investor Dashboard', href: '/investor' },
    },
    {
      title: 'Check your portfolio banner',
      description: 'See Total Invested, Est. Value, Unrealized P&L, and ROI% in the dark banner at the top.',
    },
    {
      title: 'View an ownership certificate',
      description: "In the My Investments tab, click 'View Certificate' on any vehicle card.",
    },
    {
      title: 'Toggle co-investor details',
      description: 'Click the co-investors row on a card to expand and see other investors (total amount, no individual amounts shown).',
    },
    {
      title: 'Check the Leaderboard',
      description: 'Click the Leaderboard tab. See Individual Rankings and Power FAMs.',
    },
    {
      title: 'Governance (DAO)',
      description: 'Click the Governance tab to see open votes and how the DAO works.',
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="rounded-xl overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Investor Journey</h2>
            <p className="text-amber-100 text-sm">Hold fractional LLC equity across funded vehicles</p>
          </div>
        </div>
      </div>

      {/* DB warning */}
      {!sql && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          DB not connected — check VITE_DATABASE_URL in .env
        </div>
      )}

      {/* Seed button */}
      <div className="mb-6">
        <button
          onClick={handleSeed}
          disabled={seedStatus === 'loading'}
          className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-60 transition-colors"
        >
          {seedStatus === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
          Seed This Account (Investor)
        </button>

        {seedStatus === 'success' && (
          <div className="mt-3 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-emerald-800 font-medium text-sm">Investor journey ready! Follow the steps below.</span>
          </div>
        )}

        {seedStatus === 'error' && (
          <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {seedError}
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="space-y-5">
        {steps.map((step, i) => (
          <StepItem
            key={i}
            number={i + 1}
            step={step}
            seeded={seedStatus === 'success'}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type ActiveTab = 'creator' | 'gifter' | 'investor';

// ---------------------------------------------------------------------------
// Main DevJourneyPanel
// ---------------------------------------------------------------------------

export function DevJourneyPanel() {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem('fam_dev_unlocked') === '1'
  );
  const [activeTab, setActiveTab] = useState<ActiveTab>('creator');

  const handleLock = () => {
    localStorage.removeItem('fam_dev_unlocked');
    setUnlocked(false);
  };

  if (!unlocked) {
    return <PinScreen onUnlock={() => setUnlocked(true)} />;
  }

  const tabs: { id: ActiveTab; label: string; color: string }[] = [
    { id: 'creator', label: 'Creator', color: 'rose' },
    { id: 'gifter', label: 'Gifter', color: 'indigo' },
    { id: 'investor', label: 'Investor', color: 'amber' },
  ];

  const tabActiveClass: Record<ActiveTab, string> = {
    creator: 'bg-rose-600 text-white',
    gifter: 'bg-indigo-600 text-white',
    investor: 'bg-amber-600 text-white',
  };

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dev Journey Panel</h1>
            <p className="text-gray-500 text-sm mt-1">
              Seed your account and follow step-by-step test flows for each role.
            </p>
          </div>
          <button
            onClick={handleLock}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
          >
            <Lock className="w-4 h-4" />
            Lock
          </button>
        </div>

        {/* Tab selector */}
        <div className="flex gap-3 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors border ${
                activeTab === tab.id
                  ? tabActiveClass[tab.id] + ' border-transparent shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {activeTab === 'creator' && <CreatorTab />}
          {activeTab === 'gifter' && <GifterTab />}
          {activeTab === 'investor' && <InvestorTab />}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Dev panel — not visible in production. PIN: famdev
        </p>
      </div>
    </div>
  );
}
